import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion, useScroll, useTransform, type Variants } from 'framer-motion';
import { useFluidHover } from '../hooks/useFluidHover';
import { fluidDistort } from '../utils/fluidDistort';
import type { Project } from '../data/projects';
interface ProjectCardProps {
  project: Project;
  className?: string;
}
const ASPECT: Record<Project['size'], string> = {
  large: 'aspect-[617/550]',
  small: 'aspect-[407/343]',
  wide: 'aspect-[16/10]'
};

/**
 * How far the whole card drifts against the scroll (px over its pass through the viewport).
 * Smaller visuals travel more, so the staggered rows slide past each other in layers.
 */
const CARD_DRIFT: Record<Project['size'], number> = {
  large: 16,
  small: 60,
  wide: 32
};

/**
 * "Window" parallax: the frame scrolls with the page while the picture behind it lags far behind,
 * as if it sat beyond the glass. The image layer overhangs the frame by 14% top and bottom (128% tall)
 * and travels 10% of that layer each way (≈ 13% of the frame) — just inside the overhang. Kept modest so
 * the picture is only blown up a little and stays sharp.
 */
const IMAGE_SHIFT = 10;

/** Soft, long landing used for the scroll reveal — matches the site's focus-in feel */
const REVEAL_EASE = [0.22, 1, 0.36, 1] as const;
/** The site-wide focus-in (same as focusInView): resolves from blur, fades up and settles a few px upward */
const SITE_EASE = [0.33, 1, 0.68, 1] as const;
const VISUAL_FOCUS: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
    filter: 'blur(10px)'
  },
  shown: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    // Drop the filter afterwards so it never interferes with the hover distortion
    transitionEnd: {
      filter: 'none'
    },
    transition: {
      opacity: {
        duration: 0.88,
        ease: SITE_EASE
      },
      filter: {
        duration: 1.1,
        ease: SITE_EASE
      },
      y: {
        duration: 1.2,
        ease: SITE_EASE
      }
    }
  }
};
/** One service item on hover: focuses in from a soft blur while rising a touch; leaves quicker than it arrives */
const SERVICE_ITEM: Variants = {
  off: {
    opacity: 0,
    y: 6,
    filter: 'blur(6px)',
    transition: {
      duration: 0.25,
      ease: [0.4, 0, 1, 1]
    }
  },
  on: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.55,
      ease: REVEAL_EASE
    }
  }
};
const REDUCED_ITEM: Variants = {
  off: {
    opacity: 0,
    transition: {
      duration: 0
    }
  },
  on: {
    opacity: 1,
    transition: {
      duration: 0
    }
  }
};
/** Text comes into focus once the image is mostly open */
const TEXT_FOCUS: Variants = {
  hidden: {
    opacity: 0,
    y: 12,
    filter: 'blur(8px)'
  },
  shown: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      opacity: {
        duration: 0.7,
        ease: REVEAL_EASE
      },
      filter: {
        duration: 0.8,
        ease: REVEAL_EASE
      },
      y: {
        duration: 0.9,
        ease: REVEAL_EASE
      }
    }
  }
};
/** Brand name first, the description shortly after */
const TEXT_GROUP: Variants = {
  hidden: {},
  shown: {
    transition: {
      delayChildren: 0.05,
      staggerChildren: 0.14
    }
  }
};

/** Visual with the client label + title below. Two-layer parallax: the card drifts, the image glides inside its frame. */
export function ProjectCard({
  project,
  className = ''
}: ProjectCardProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });

  // Phones: no card drift (it differs per size and would make the single-column gaps uneven)
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setNarrow(mq.matches);
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  const drift = narrow ? 0 : CARD_DRIFT[project.size];
  const cardY = useTransform(scrollYProgress, [0, 1], [drift, -drift]);
  const imageY = useTransform(scrollYProgress, [0, 1], [`-${IMAGE_SHIFT}%`, `${IMAGE_SHIFT}%`]);

  // Liquid hover (mouse only): the WebGL flow-field effect, or the lighter SVG ripple if the image host
  // doesn't allow WebGL to read the picture
  const fluid = useFluidHover(reduce);
  const frameRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const svgFallback = useRef(false);
  const pointerInside = useRef(false);
  const onFrameEnter = (e: React.PointerEvent<HTMLDivElement>) => {
    // No distortion on phone-sized screens
    if (reduce || e.pointerType !== 'mouse' || window.innerWidth < 768 || !frameRef.current || !imgRef.current) return;
    pointerInside.current = true;
    const {
      clientX,
      clientY
    } = e;
    fluidDistort().enter(frameRef.current, imgRef.current, clientX, clientY).then(ok => {
      if (ok || !pointerInside.current) return;
      svgFallback.current = true;
      fluid.onEnter(e);
    });
  };
  const onFrameMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!frameRef.current || e.pointerType !== 'mouse' || !pointerInside.current) return;
    if (svgFallback.current) fluid.onMove(e);else fluidDistort().move(frameRef.current, e.clientX, e.clientY);
  };
  const onFrameLeave = () => {
    pointerInside.current = false;
    if (frameRef.current) fluidDistort().leave(frameRef.current);
    fluid.onLeave();
  };

  // Services line: revealed on hover/focus; always shown on touch screens (no hover there)
  const [hovered, setHovered] = useState(false);
  const [noHover, setNoHover] = useState(false);
  useEffect(() => {
    // Touch screens and phone-sized screens: always visible
    const mq = window.matchMedia('(hover: none), (max-width: 767px)');
    const sync = () => setNoHover(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  const showServices = hovered || noHover;

  // Text reveal is driven by its own one-shot in-view flag (an explicit `animate`), so it plays exactly once
  // and can never be re-triggered by the card's own reveal propagating down the variant tree
  const textRef = useRef<HTMLDivElement>(null);
  const textInView = useInView(textRef, {
    once: true,
    amount: 1,
    margin: '0px 0px -8% 0px'
  });
  return <motion.article ref={ref} className={className} style={reduce ? undefined : {
    y: cardY
  }} data-id="element-148" data-mp="1w050q2sru2qf">
      {/* Reveal: the visual blurs into view; the text below focuses in on its own trigger */}
      <motion.div initial={reduce ? false : 'hidden'} whileInView="shown" viewport={{
      once: true,
      amount: 0.25
    }} data-id="element-149" data-mp="13br1jjpy50da">
        <a href="#work" className="group block focus-visible:outline-none" onPointerEnter={e => e.pointerType === 'mouse' && setHovered(true)} onPointerLeave={() => setHovered(false)} onFocus={() => setHovered(true)} onBlur={() => setHovered(false)} data-id="element-150" data-mp="18aaonnga0i78">
          {/* Scroll reveal: the visual blurs into view, like everything else on the page */}
          {/* Phones: square corners, bleeding to both screen edges (cancels the section's 12px gutter) */}
          <motion.div variants={VISUAL_FOCUS} className="-mx-3 md:mx-0 md:rounded-[16px]" data-id="element-151" data-mp="83osbixp69oc">
          <motion.div ref={frameRef} className={`${ASPECT[project.size]} relative w-full overflow-hidden bg-neutral-100 md:rounded-[16px] group-focus-visible:outline group-focus-visible:outline-2 group-focus-visible:outline-offset-4 group-focus-visible:outline-black`} onPointerEnter={onFrameEnter} onPointerMove={onFrameMove} onPointerLeave={onFrameLeave} data-id="element-152" data-mp="3b5dyjr6raih">
            {/* Fallback ripple (SVG): noise masked to a soft spot under the pointer, neutral grey elsewhere */}
            <svg aria-hidden="true" className="absolute h-0 w-0" data-id="element-153" data-mp="3blgqjf2ffpuz">
              <filter id={fluid.filterId} x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB" data-id="element-154" data-mp="1eq100qs5we92">
                <feTurbulence ref={fluid.turbRef} type="fractalNoise" baseFrequency="0.007 0.009" numOctaves="1" seed="3" result="noise" data-id="element-155" data-mp="19ssjeumnwi9x" />
                <feImage ref={fluid.spotRef} href={fluid.spotUrl} x="0" y="0" width="0" height="0" preserveAspectRatio="none" result="spot" data-id="element-156" data-mp="nlgltmthyox0" />
                <feComposite in="noise" in2="spot" operator="in" result="localNoise" data-id="element-157" data-mp="9tjw1pfawr3s" />
                <feFlood floodColor="rgb(128,128,128)" result="neutral" data-id="element-158" data-mp="duvdh7o0u60w" />
                <feComposite in="localNoise" in2="neutral" operator="over" result="map" data-id="element-159" data-mp="3w1t8kl4490c9" />
                <feDisplacementMap ref={fluid.dispRef} in="SourceGraphic" in2="map" scale="0" xChannelSelector="R" yChannelSelector="G" data-id="element-160" data-mp="4v8nrj4nmono" />
              </filter>
            </svg>
            {/* Oversized so the image can glide (and ripple) inside the frame without exposing its edges */}
            <motion.div ref={fluid.targetRef} className="absolute -inset-x-[2%] -inset-y-[14%]" style={reduce ? undefined : {
              y: imageY
            }} data-id="element-161" data-mp="3r49v3nf8srld">
              <img ref={imgRef} src={project.image} alt={project.alt} loading="lazy" decoding="async" className="h-full w-full object-cover [backface-visibility:hidden]" data-id="element-162" data-mp="1p8r5vi3xsba2" />
            </motion.div>
          </motion.div>
          </motion.div>
          {/* Text has its own trigger: it only focuses in once the text itself is fully on screen
              (a little above the bottom edge), not when the image above it arrives */}
          <motion.div ref={textRef} variants={TEXT_GROUP} initial={reduce ? false : 'hidden'} animate={textInView || reduce ? 'shown' : 'hidden'} data-id="element-163" data-mp="3oitrwn81hmwl">
          {/* Brand name first, the description a beat later; service areas fade in underneath on hover */}
          {/* The blur animates on a wrapper, never on the [data-ink] text itself: [data-ink] has a 700ms CSS
              transition on `filter` (for the dark-mode switch), which made the blur play a second, delayed time */}
          <motion.div variants={TEXT_FOCUS} className="mt-[clamp(1rem,1.6vw,1.6rem)]" data-id="element-164" data-mp="35harwhr9ygp7">
            <h3 data-ink="" className="text-[clamp(1.4rem,2.05vw,2.1rem)] font-bold leading-[1.1] tracking-[-0.03em] text-black" data-id="element-165" data-mp="30fd214odcnna">
              {project.client}
            </h3>
          </motion.div>
          <motion.div variants={TEXT_FOCUS} className="mt-[clamp(0.5rem,0.8vw,0.85rem)]" data-id="element-166" data-mp="2rhsy1nz4gd8z">
            <p data-ink="" className="text-[clamp(0.95rem,1.15vw,1.375rem)] font-bold leading-tight tracking-[-0.03em] text-black" data-id="element-167" data-mp="3o57evm8ouc7j">
              {project.summary}
            </p>
          </motion.div>
          {/* Space is always reserved, so the reveal never shifts the layout. Each service focuses in on its own,
              left to right, at full colour (white on black). */}
          {/* Wrapper = third beat of the text reveal: on phones/touch (services always shown) the line blurs in
              right after the description; on desktop it only wraps the reserved, still-empty space */}
          <motion.div variants={TEXT_FOCUS} data-id="element-168" data-mp="eatlkiitbm63">
          <motion.p data-ink="" className="mt-[clamp(0.6rem,0.9vw,0.95rem)] text-[clamp(0.95rem,1.15vw,1.375rem)] font-bold leading-tight tracking-[-0.03em] text-black opacity-50" initial={false} animate={showServices ? 'on' : 'off'} variants={{
              on: {
                transition: {
                  staggerChildren: reduce ? 0 : 0.05
                }
              },
              off: {
                transition: {
                  staggerChildren: reduce ? 0 : 0.025,
                  staggerDirection: -1
                }
              }
            }} data-id="element-169" data-mp="j8le9era7cbc">
            <span className="sr-only" data-id="element-170" data-mp="16y7fs8v4m3mi">Services: </span>
            {project.services.map((s, i) => <motion.span key={s} variants={reduce ? REDUCED_ITEM : SERVICE_ITEM} className="inline-block whitespace-pre" data-id="element-171" data-mp="1bx0shz2l9qlm" data-mpi={i}>
                {i > 0 && <span aria-hidden="true" className="px-[0.3em] text-neutral-400" data-id="element-172" data-mp="1g0gjaerz8wqe">
                    /
                  </span>}
                {s}
              </motion.span>)}
          </motion.p>
          </motion.div>
          </motion.div>
        </a>
      </motion.div>
    </motion.article>;
}